import React, { createContext, useState, useContext, useEffect } from 'react';
import { auth } from '../firebaseConfig';
import {
  getUserOrganizations,
  getUserCurrentOrganization,
  createOrganization,
  switchOrganization as switchOrgService,
  getUserInvitations
} from '../services/organizationService';

const OrganizationContext = createContext();

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
};

export const OrganizationProvider = ({ children }) => {
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Load user and organizations on auth state change
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        await loadOrganizations(firebaseUser.uid, firebaseUser.email);
      } else {
        setUser(null);
        setCurrentOrganization(null);
        setOrganizations([]);
        setPendingInvitations([]);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const loadOrganizations = async (userId, userEmail) => {
    try {
      setLoading(true);
      const [orgs, currentOrg, invites] = await Promise.all([
        getUserOrganizations(userId),
        getUserCurrentOrganization(userId),
        getUserInvitations(userEmail)
      ]);

      setOrganizations(orgs);
      setPendingInvitations(invites);

      // If user has a current org, set it
      if (currentOrg) {
        setCurrentOrganization(currentOrg);
      } 
      // If user has orgs but no current org, set the first one
      else if (orgs.length > 0) {
        setCurrentOrganization(orgs[0]);
        await switchOrgService(userId, orgs[0].id);
      }
      // If no organizations, create a personal organization
      else {
        const displayName = auth.currentUser?.displayName || userEmail.split('@')[0];
        const personalOrg = await createOrganization(
          `${displayName}'s Workspace`,
          userId,
          userEmail
        );
        setOrganizations([personalOrg]);
        setCurrentOrganization(personalOrg);
      }
    } catch (error) {
      console.error('Error loading organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const switchOrganization = async (orgId) => {
    try {
      const org = organizations.find(o => o.id === orgId);
      if (org && user) {
        setCurrentOrganization(org);
        await switchOrgService(user.uid, orgId);
      }
    } catch (error) {
      console.error('Error switching organization:', error);
      throw error;
    }
  };

  const refreshOrganizations = async () => {
    if (user) {
      await loadOrganizations(user.uid, user.email);
    }
  };

  const value = {
    currentOrganization,
    organizations,
    pendingInvitations,
    loading,
    switchOrganization,
    refreshOrganizations
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

